/**
 * Calendar-day arithmetic, in the user's own timezone.
 *
 * Every date the planner stores is a YYYY-MM-DD *calendar day* — the square a
 * human would point at on a wall calendar. It is not an instant, and it has no
 * timezone of its own. The only correct way to turn a `Date` into one is to
 * read the local fields.
 *
 * `toISOString().slice(0, 10)` renders the day **in UTC**, which is a different
 * day from the user's for part of every day:
 *
 *   - west of Greenwich, from evening until local midnight (Montreal: 20:00 on)
 *   - east of Greenwich, from local midnight until the offset runs out
 *
 * That is #679: at 21:19 on Monday the 14th in Montreal the app called it
 * Tuesday the 15th, so the "Aujourd'hui · Souper" hero showed tomorrow's
 * dinner. The window opens at 20:00 — exactly when someone asks what's for
 * supper.
 *
 * Bare `new Date('2026-09-14')` is the same trap from the other side: a
 * date-only string is parsed as UTC midnight, which is the *previous* day in
 * any negative offset. `parseIsoDate` anchors at local noon instead, far enough
 * from both midnights that no DST shift can move the day.
 *
 * This module is the single source of truth. `localDate.guard.test.ts` fails
 * the build if a UTC date slice reappears anywhere in `src/`.
 */

/** Render a `Date` as the calendar day it falls on locally. */
export function toIsoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Today, as the user's calendar sees it. */
export function todayIso(now: Date = new Date()): string {
  return toIsoDate(now);
}

/**
 * Parse a YYYY-MM-DD day into a `Date` sitting at local noon.
 *
 * Noon, not midnight: a DST transition can move midnight across the date
 * boundary, and `new Date('2026-09-14')` (no time part) is UTC midnight, which
 * is 20:00 the previous day in Montreal. Noon survives both.
 */
export function parseIsoDate(iso: string): Date {
  return new Date(`${iso}T12:00:00`);
}

/**
 * Parse a stored date that may be either a calendar day or an instant.
 *
 * Several stores hold both: a cook-log entry's `date` is the day the cook
 * picked (`2026-10-05`), a planned meal's `cookedAt` is a timestamp. A day goes
 * through `parseIsoDate`; anything carrying a time is already an instant.
 */
export function parseDayOrInstant(value: string): Date {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? parseIsoDate(value) : new Date(value);
}

/** Shift a calendar day by `n` days. Exact across DST transitions. */
export function addDays(iso: string, n: number): string {
  const d = parseIsoDate(iso);
  d.setDate(d.getDate() + n);
  return toIsoDate(d);
}

/**
 * The Monday of the week containing `iso`.
 *
 * Sunday belongs to the week that is ending, not the one about to start — the
 * planner's weeks run Monday through Sunday.
 */
export function weekStartOf(iso: string): string {
  const d = parseIsoDate(iso);
  const dow = d.getDay();
  return addDays(iso, dow === 0 ? -6 : 1 - dow);
}

/** The Monday of the week the user is living in right now. */
export function currentWeekStart(now: Date = new Date()): string {
  return weekStartOf(todayIso(now));
}

/** Position of `iso` within the week starting `weekStart`, or null if outside it. */
export function dayIndexInWeek(weekStart: string, iso: string): number | null {
  for (let di = 0; di < 7; di++) {
    if (addDays(weekStart, di) === iso) return di;
  }
  return null;
}

/** Minutes past local midnight — the "how far into today are we" half of a clock read. */
export function minutesIntoDay(now: Date = new Date()): number {
  return now.getHours() * 60 + now.getMinutes();
}
